import type { ReactNode } from 'react';
import { LinearProgress, Stack, Typography } from '@mui/material';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { SEMANTIC_COLOR_VARS, type SemanticColor } from '../../../shared/components/semanticColor';

/** One project-health metric tile — icon + label, a big value, and an optional progress bar/description. */
export function OverviewCard({
  icon,
  label,
  value,
  description,
  progressPercent,
  color,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  description?: string;
  /** 0-100; renders a progress bar under the value when provided. */
  progressPercent?: number;
  color: SemanticColor;
}) {
  const vars = SEMANTIC_COLOR_VARS[color];
  return (
    <GlassPanel sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconTile icon={icon} color={color} size="sm" />
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1, letterSpacing: '0.06em' }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontWeight: 800, fontSize: 24, color: vars.main, lineHeight: 1.2 }}>{value}</Typography>
      {progressPercent != null && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, progressPercent))}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: vars.soft,
            '& .MuiLinearProgress-bar': { backgroundColor: vars.main, borderRadius: 3 },
          }}
        />
      )}
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </GlassPanel>
  );
}
